import React, { useRef, useEffect, useState, useCallback } from 'react';
import useGameBase from '../../hooks/useGameBase';

// ─── Canvas constants ─────────────────────────────────────────────────────────
const VIRTUAL_SIZE   = 600;
const MAX_DRAG       = 110;   // max drag pixels (virtual) = full power

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getVirtualCoords(e, canvas) {
  const rect   = canvas.getBoundingClientRect();
  const scaleX = VIRTUAL_SIZE / rect.width;
  const scaleY = VIRTUAL_SIZE / rect.height;
  const src    = e.touches ? e.touches[0] : e;
  return {
    x: (src.clientX - rect.left) * scaleX,
    y: (src.clientY - rect.top)  * scaleY,
  };
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function KnockoutGame({
  roomId, isHost, gameState, gamePlayers = [], user = {}, onGameAction,
}) {
  const { submitAction } = useGameBase({ gameState, gamePlayers, user, roomId });

  const canvasRef = useRef(null);
  const rafRef    = useRef(null);

  // Drag state: are we currently dragging our piece?
  const dragActiveRef = useRef(false);

  // Local aim (our own — shown as arrow before the timer fires)
  const localAimRef      = useRef(null);  // { angle, power } always current
  const [localAim, setLocalAim] = useState(null);
  const lastSentAimRef   = useRef(null);  // throttle: avoid flooding server
  const aimSendTimerRef  = useRef(null);

  // Client-side physics animation refs
  const simRef     = useRef(null);  // { pieces, friction, cx, cy, radius, pieceRadius }
  const phaseRef   = useRef('waiting');

  // Live-data refs — always current, read by draw loop without closure staleness
  const playersRef    = useRef([]);
  const arenaRef      = useRef({ cx: 300, cy: 300, radius: 250, pieceRadius: 18 });
  const myIdRef       = useRef(null);
  const userRef       = useRef({});
  const canAimRef     = useRef(false);
  const myPlayerRef   = useRef(null);
  const gameStateRef  = useRef(null);  // always points to latest gameState

  // Planning countdown timer
  const [timeLeft, setTimeLeft] = useState(null);
  const timerIntervalRef = useRef(null);

  // ── Derived values ──────────────────────────────────────────────────────────
  const phase         = gameState?.phase       || 'waiting';
  const players       = gameState?.players     || [];
  const arenaConfig   = gameState?.arenaConfig || { cx: 300, cy: 300, radius: 250, pieceRadius: 18 };

  const myId     = user?.socketId;
  const myPlayer = players.find(
    p => p.socketId === myId || (user?.userId && p.userId === user.userId)
  );
  const isEliminated = !!myPlayer?.eliminated;
  const canAim       = phase === 'planning' && !isEliminated;
  const scores       = gameState?.scores || {};

  // ── Sync live-data refs every render so draw loop never reads stale closures ──
  playersRef.current  = players;
  arenaRef.current    = arenaConfig;
  myIdRef.current     = myId;
  userRef.current     = user;
  canAimRef.current   = canAim;
  myPlayerRef.current = myPlayer;
  phaseRef.current    = phase;
  gameStateRef.current = gameState;

  // ── Action helper ───────────────────────────────────────────────────────────
  const sendAction = useCallback(async (action) => {
    try {
      if (typeof onGameAction === 'function') onGameAction(action);
      else await submitAction(action);
    } catch (e) {
      console.error('KnockoutGame action error', e);
    }
  }, [onGameAction, submitAction]);

  // ── Reset local aim when planning phase starts ──────────────────────────────
  useEffect(() => {
    if (phase === 'planning') {
      clearTimeout(aimSendTimerRef.current);
      lastSentAimRef.current = null;
      localAimRef.current = null;
      setLocalAim(null);
      dragActiveRef.current = false;
    }
  }, [phase]);

  // ── Initialise client-side simulation when resolving starts ────────────────
  // Uses gameStateRef (updated every render) so it always reads the latest state,
  // even if React batched renders or the effect closure is stale.
  useEffect(() => {
    const gs = gameStateRef.current;  // always current

    if (phase === 'resolving') {
      const { cx, cy, radius, pieceRadius } = gs?.arenaConfig || { cx: 300, cy: 300, radius: 250, pieceRadius: 18 };
      const friction = gs?.frictionValue ?? 0.93;
      const lv = gs?.launchVectors || {};
      const pieces = (gs?.players || []).map(p => ({
        socketId:   p.socketId,
        userId:     p.userId,
        username:   p.username,
        color:      p.color,
        x:          p.x,
        y:          p.y,
        vx:         lv[p.socketId]?.vx ?? 0,
        vy:         lv[p.socketId]?.vy ?? 0,
        eliminated: !!p.eliminated,
      }));
      simRef.current = { friction, cx, cy, radius, pieceRadius, pieces };
    } else {
      simRef.current = null;
    }
  }, [phase]);  // gameStateRef is always current; no closure staleness risk

  // ── Planning countdown timer ─────────────────────────────────────────────────────────
  useEffect(() => {
    clearInterval(timerIntervalRef.current);
    if (phase === 'planning' && gameState?.planningEndsAt) {
      const tick = () => {
        const rem = Math.max(0, Math.ceil((gameState.planningEndsAt - Date.now()) / 1000));
        setTimeLeft(rem);
        if (rem <= 0) clearInterval(timerIntervalRef.current);
      };
      tick();
      timerIntervalRef.current = setInterval(tick, 250);
    } else {
      setTimeLeft(null);
    }
    return () => clearInterval(timerIntervalRef.current);
  }, [phase, gameState?.planningEndsAt]);

  // ── Render loop ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let running = true;

    const draw = () => {
      if (!running) return;
      rafRef.current = requestAnimationFrame(draw);

      const ctx  = canvas.getContext('2d');
      const { cx, cy, radius, pieceRadius } = arenaRef.current;

      ctx.clearRect(0, 0, VIRTUAL_SIZE, VIRTUAL_SIZE);

      // ── Arena background ──
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.fillStyle = '#0f172a';
      ctx.fill();

      // ── Subtle grid inside arena ──
      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.clip();
      ctx.strokeStyle = 'rgba(255,255,255,0.06)';
      ctx.lineWidth = 1;
      for (let gx = cx % 50; gx < VIRTUAL_SIZE; gx += 50) {
        ctx.beginPath(); ctx.moveTo(gx, 0); ctx.lineTo(gx, VIRTUAL_SIZE); ctx.stroke();
      }
      for (let gy = cy % 50; gy < VIRTUAL_SIZE; gy += 50) {
        ctx.beginPath(); ctx.moveTo(0, gy); ctx.lineTo(VIRTUAL_SIZE, gy); ctx.stroke();
      }
      ctx.restore();

      // ── Arena border: crisp defined edge ──
      // Outer void ring so the boundary stands out sharply
      ctx.beginPath();
      ctx.arc(cx, cy, radius + 6, 0, Math.PI * 2);
      ctx.arc(cx, cy, radius, 0, Math.PI * 2, true);
      ctx.fillStyle = 'rgba(0,0,0,0.55)';
      ctx.fill();
      // Hard white border line
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.strokeStyle = '#e2e8f0';
      ctx.lineWidth = 3;
      ctx.stroke();

      // ── Centre dot ──
      ctx.beginPath();
      ctx.arc(cx, cy, 5, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255,255,255,0.15)';
      ctx.fill();

      // ── Determine display positions ──
      let displayPieces;
      const sim = simRef.current;
      if (sim && phaseRef.current === 'resolving') {
        // ── Advance physics: move + elastic collisions ──
        const alive = sim.pieces.filter(p => !p.eliminated);

        // Move all alive pieces
        for (const p of alive) {
          p.x += p.vx;
          p.y += p.vy;
          p.vx *= sim.friction;
          p.vy *= sim.friction;
          if (Math.hypot(p.x - sim.cx, p.y - sim.cy) > sim.radius) {
            p.eliminated = true;
          }
        }

        // Elastic pair-wise collisions (mirrors server logic)
        const stillAlive = alive.filter(p => !p.eliminated);
        for (let i = 0; i < stillAlive.length; i++) {
          for (let j = i + 1; j < stillAlive.length; j++) {
            const a = stillAlive[i];
            const b = stillAlive[j];
            const dx   = b.x - a.x;
            const dy   = b.y - a.y;
            const dist = Math.hypot(dx, dy);
            const minD = sim.pieceRadius * 2;
            if (dist < minD && dist > 0.001) {
              const nx      = dx / dist;
              const ny      = dy / dist;
              const overlap = minD - dist;
              a.x -= nx * overlap * 0.5;  a.y -= ny * overlap * 0.5;
              b.x += nx * overlap * 0.5;  b.y += ny * overlap * 0.5;
              if (Math.hypot(a.x - sim.cx, a.y - sim.cy) > sim.radius) a.eliminated = true;
              if (Math.hypot(b.x - sim.cx, b.y - sim.cy) > sim.radius) b.eliminated = true;
              const relVx = a.vx - b.vx;
              const relVy = a.vy - b.vy;
              const dot   = relVx * nx + relVy * ny;
              if (dot > 0) {
                a.vx -= dot * nx;  a.vy -= dot * ny;
                b.vx += dot * nx;  b.vy += dot * ny;
              }
            }
          }
        }

        displayPieces = sim.pieces;
      } else {
        displayPieces = playersRef.current;
      }

      // ── Draw each piece ──
      for (const p of displayPieces) {
        const isMe = p.socketId === myIdRef.current || (userRef.current?.userId && p.userId === userRef.current.userId);
        const out  = !!p.eliminated;

        ctx.save();
        ctx.globalAlpha = out ? 0.25 : 1;

        if (!out) {
          ctx.shadowColor = 'rgba(0,0,0,0.6)';
          ctx.shadowBlur  = 10;
        }

        // Main filled circle
        ctx.beginPath();
        ctx.arc(p.x, p.y, pieceRadius, 0, Math.PI * 2);
        ctx.fillStyle = p.color || '#888';
        ctx.fill();

        // White ring for local player
        if (isMe && !out) {
          ctx.beginPath();
          ctx.arc(p.x, p.y, pieceRadius + 3, 0, Math.PI * 2);
          ctx.strokeStyle = '#fff';
          ctx.lineWidth = 2.5;
          ctx.stroke();
        }

        // Initial letter
        ctx.shadowBlur = 0;
        ctx.fillStyle  = '#fff';
        ctx.font       = `bold ${Math.round(pieceRadius * 0.85)}px sans-serif`;
        ctx.textAlign  = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText((p.username || '?').charAt(0).toUpperCase(), p.x, p.y);

        ctx.restore();
      }

      // ── Aim arrow for local player (planning phase) ──
      const aim = localAimRef.current;
      const _mp = myPlayerRef.current;
      if (aim && canAimRef.current && _mp && !_mp.eliminated) {
        const px = _mp.x;
        const py = _mp.y;
        const arrowLen  = aim.power * MAX_DRAG;
        const endX      = px + arrowLen * Math.cos(aim.angle);
        const endY      = py + arrowLen * Math.sin(aim.angle);
        const headAngle = Math.atan2(endY - py, endX - px);
        const headLen   = 14;

        // Dashed line
        ctx.save();
        ctx.strokeStyle = 'rgba(255,255,255,0.85)';
        ctx.lineWidth   = 3;
        ctx.setLineDash([7, 5]);
        ctx.beginPath();
        ctx.moveTo(px, py);
        ctx.lineTo(endX, endY);
        ctx.stroke();
        ctx.setLineDash([]);

        // Arrowhead
        ctx.beginPath();
        ctx.moveTo(endX, endY);
        ctx.lineTo(
          endX - headLen * Math.cos(headAngle - 0.42),
          endY - headLen * Math.sin(headAngle - 0.42)
        );
        ctx.lineTo(
          endX - headLen * Math.cos(headAngle + 0.42),
          endY - headLen * Math.sin(headAngle + 0.42)
        );
        ctx.closePath();
        ctx.fillStyle = 'rgba(255,255,255,0.95)';
        ctx.fill();

        // Power arc around piece (green → red based on power)
        const arcStart = -Math.PI / 2;
        const arcEnd   = arcStart + aim.power * Math.PI * 2;
        const hue      = 120 - Math.round(aim.power * 120);
        ctx.beginPath();
        ctx.arc(px, py, arenaRef.current.pieceRadius + 5, arcStart, arcEnd);
        ctx.strokeStyle = `hsl(${hue}, 100%, 60%)`;
        ctx.lineWidth   = 4;
        ctx.stroke();

        ctx.restore();
      }
    };

    draw();
    return () => {
      running = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // Draw loop reads all live data via refs — no deps needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Drag handlers ───────────────────────────────────────────────────────────
  // Players tap/drag ANYWHERE on the canvas during planning.
  // Direction is computed from their piece → tap point.
  // Send aim to server immediately (called on down + move + up)
  const flushAim = useCallback((aim) => {
    if (!aim || aim.power < 0.02) return;
    clearTimeout(aimSendTimerRef.current);
    aimSendTimerRef.current = setTimeout(async () => {
      lastSentAimRef.current = aim;
      try {
        await sendAction({ type: 'setAim', angle: aim.angle, power: aim.power });
      } catch (e) { /* ignored */ }
    }, 0);
  }, [sendAction]);

  const handlePointerDown = useCallback((e) => {
    if (!canAimRef.current) return;
    const mp = myPlayerRef.current;
    if (!mp || mp.eliminated) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    dragActiveRef.current = true;
    e.preventDefault();

    // Set an initial aim immediately on press (towards tap point from piece)
    const pos = getVirtualCoords(e, canvas);
    const dx  = pos.x - mp.x;
    const dy  = pos.y - mp.y;
    const raw = Math.hypot(dx, dy);
    if (raw > 2) {
      const angle  = Math.atan2(dy, dx);
      const power  = Math.min(raw / MAX_DRAG, 1);
      const newAim = { angle, power };
      localAimRef.current = newAim;
      setLocalAim(newAim);
      flushAim(newAim);  // ← send to server immediately on tap
    }
  }, [flushAim]);

  const handlePointerMove = useCallback((e) => {
    const mp = myPlayerRef.current;
    if (!dragActiveRef.current || !mp) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const pos   = getVirtualCoords(e, canvas);
    const dx    = pos.x - mp.x;
    const dy    = pos.y - mp.y;
    const raw   = Math.hypot(dx, dy);
    if (raw < 2) return;
    const angle  = Math.atan2(dy, dx);
    const power  = Math.min(raw / MAX_DRAG, 1);
    const newAim = { angle, power };
    localAimRef.current = newAim;
    setLocalAim(newAim);
    flushAim(newAim);  // ← keep server updated as user drags
    e.preventDefault();
  }, [flushAim]);

  const handlePointerUp = useCallback(async () => {
    dragActiveRef.current = false;
    const aim = localAimRef.current;
    if (aim && aim.power > 0.02) {
      // Final send on release — ensures server has the most recent aim
      clearTimeout(aimSendTimerRef.current);
      await sendAction({ type: 'setAim', angle: aim.angle, power: aim.power });
    }
  }, [sendAction]);

  // ── Attach touch handlers as non-passive so preventDefault works ────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.addEventListener('touchstart', handlePointerDown, { passive: false });
    canvas.addEventListener('touchmove',  handlePointerMove,  { passive: false });
    canvas.addEventListener('touchend',   handlePointerUp,    { passive: false });
    return () => {
      canvas.removeEventListener('touchstart', handlePointerDown);
      canvas.removeEventListener('touchmove',  handlePointerMove);
      canvas.removeEventListener('touchend',   handlePointerUp);
    };
  }, [handlePointerDown, handlePointerMove, handlePointerUp]);

  // ── Derived display helpers ─────────────────────────────────────────────────
  const activePlayers      = players.filter(p => !p.eliminated);
  const elimNames          = (gameState?.eliminatedThisRound || [])
    .map(id => players.find(p => p.socketId === id)?.username || 'Someone')
    .join(' & ');
  const winnerName = (() => {
    const w = (gameState?.winners || [])[0];
    return w ? players.find(p => p.socketId === w)?.username || 'Winner' : null;
  })();

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col items-center gap-3 p-2 w-full select-none">

      {/* ── Top status bar ── */}
      <div className="flex items-center justify-between w-full max-w-lg px-1">
        <div className="text-sm font-semibold text-gray-800 dark:text-gray-200">
          {phase === 'waiting'   && 'Waiting to start'}
          {phase === 'planning'  && `Round ${gameState?.round || 0}`}
          {phase === 'resolving' && `Round ${gameState?.round || 0}`}
          {phase === 'roundEnd'  && `Round ${gameState?.round || 0} — Results`}
          {phase === 'finished'  && 'Game Over'}
        </div>

        {phase === 'planning' && timeLeft !== null && (
          <div className={`text-lg font-bold tabular-nums ${
            timeLeft <= 3 ? 'text-red-600 dark:text-red-400 animate-pulse' : 'text-yellow-600 dark:text-yellow-300'
          }`}>
            {timeLeft}s
          </div>
        )}

        <div className="text-sm text-gray-600 dark:text-gray-400">
          {activePlayers.length} / {players.length} alive
        </div>
      </div>

      {/* ── Canvas ── */}
      <div className="relative w-full max-w-lg">
        <canvas
          ref={canvasRef}
          width={VIRTUAL_SIZE}
          height={VIRTUAL_SIZE}
          style={{ width: '100%', height: 'auto', borderRadius: '14px', touchAction: 'none', display: 'block' }}
          className="shadow-2xl"
          onMouseDown={handlePointerDown}
          onMouseMove={handlePointerMove}
          onMouseUp={handlePointerUp}
          onMouseLeave={handlePointerUp}
        />

        {/* ── Overlay: waiting ── */}
        {phase === 'waiting' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 rounded-2xl">
            <div className="text-white text-2xl font-extrabold mb-2">💥 Knockout</div>
            <div className="text-gray-300 text-sm px-8 text-center max-w-xs">
              Drag your piece to aim. Last one on the arena wins!
            </div>
          </div>
        )}

        {/* ── Overlay: launching ── */}
        {phase === 'resolving' && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-black/70 text-white px-5 py-1.5 rounded-full text-sm font-bold shadow">
            🚀 Launching!
          </div>
        )}

        {/* ── Overlay: round end ── */}
        {phase === 'roundEnd' && elimNames && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-red-700/85 text-white px-5 py-1.5 rounded-full text-sm font-bold shadow">
            ❌ {elimNames} knocked out!
          </div>
        )}
        {phase === 'roundEnd' && !elimNames && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-green-700/85 text-white px-5 py-1.5 rounded-full text-sm font-bold shadow">
            Everyone survived!
          </div>
        )}

        {/* ── Overlay: finished ── */}
        {phase === 'finished' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 rounded-2xl px-4 py-5">
            <div className="text-5xl mb-2">🏆</div>
            <div className="text-white text-xl font-extrabold mb-0.5">
              {winnerName ? `${winnerName} wins!` : 'All knocked out!'}
            </div>
            <div className="text-gray-300 text-xs mb-4">
              {winnerName ? 'Last player standing!' : 'Nobody survived the final round.'}
            </div>

            {/* Final Scores */}
            <div className="w-full max-w-xs">
              <div className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-2 text-center">Final Scores</div>
              <div className="space-y-1.5">
                {[...players]
                  .sort((a, b) => (scores[b.socketId] || 0) - (scores[a.socketId] || 0))
                  .map((p, idx) => {
                    const isWinner = (gameState?.winners || []).includes(p.socketId);
                    const score    = scores[p.socketId] || 0;
                    const medal    = idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : null;
                    return (
                      <div
                        key={p.socketId}
                        className={[
                          'flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm',
                          isWinner ? 'bg-yellow-600/80 text-white font-bold' : 'bg-gray-700/60 text-gray-200',
                        ].join(' ')}
                      >
                        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: p.color }} />
                        <span className="flex-1 truncate">{medal && <span className="mr-1">{medal}</span>}{p.username}</span>
                        <span className="font-semibold tabular-nums">{score} pt{score !== 1 ? 's' : ''}</span>
                      </div>
                    );
                  })
                }
              </div>
            </div>
          </div>
        )}

        {/* ── Overlay: eliminated spectator ── */}
        {phase === 'planning' && isEliminated && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 text-red-300 px-4 py-1.5 rounded-full text-xs font-semibold shadow">
            You're out — watching
          </div>
        )}

        {/* ── Overlay: aim hint ── */}
        {phase === 'planning' && !isEliminated && !localAim && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 text-gray-200 px-4 py-1.5 rounded-full text-xs shadow">
            Tap anywhere to aim your piece
          </div>
        )}
        {phase === 'planning' && !isEliminated && localAim && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-green-700/80 text-white px-4 py-1.5 rounded-full text-xs font-semibold shadow pointer-events-none">
            ✓ Aim set — tap again to adjust
          </div>
        )}
      </div>

      {/* ── Players ── */}
      <div className="w-full max-w-lg bg-gray-800/60 dark:bg-gray-900/60 rounded-xl p-3 shadow">
        <div className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-2">
          Players
        </div>
        <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
          {players.map(p => {
            const isMe = p.socketId === myId || (user?.userId && p.userId === user.userId);
            return (
              <div
                key={p.socketId}
                className={[
                  'flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm transition',
                  p.eliminated ? 'opacity-40 bg-gray-700/30' : 'bg-gray-700/60',
                  isMe ? 'ring-1 ring-white/40' : '',
                ].join(' ')}
              >
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: p.color }}
                />
                <div className="flex-1 truncate font-medium text-gray-100 text-xs sm:text-sm">
                  {p.username}
                </div>
                {p.eliminated
                  ? <div className="text-xs text-red-400 font-semibold">OUT</div>
                  : <div className="text-xs text-green-400 font-semibold">✓</div>
                }
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Guide ────────────────────────────────────────────────────────────────────
KnockoutGame.Guide = function KnockoutGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">How to Play Knockout</h3>
      <ul className="list-disc ml-6 space-y-1">
        <li><strong>Objective:</strong> Be the last player remaining on the circular arena. Knock opponents off the edge by colliding with them.</li>
        <li><strong>Controls:</strong> During the planning phase, tap (or click) anywhere on the arena. An arrow appears from your piece — that is your launch direction. The further from your piece you tap, the more power.</li>
        <li><strong>Launching:</strong> Once the timer counts down to zero, every piece launches simultaneously. There is no taking turns — everyone moves at once!</li>
        <li><strong>Physics:</strong> Pieces slide across the arena with friction. When two pieces collide, momentum transfers — a powerful head-on hit can send the other piece flying off the edge!</li>
        <li><strong>Elimination:</strong> Any piece whose centre crosses the circular boundary is eliminated for the rest of the game.</li>
        <li><strong>Surface:</strong> The host can choose Icy (pieces barely slow down), Normal, or Rough (stops quickly).</li>
        <li><strong>Scoring:</strong> Winner gets 3 points, anyone knocked out in the final round gets 1 point, everyone else gets 0.</li>
      </ul>
    </div>
  );
};
